import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchCompletedTask } from "../../../service/Task";
import AppContext from "../../../context/AppContext";
import { Spinner } from "../../../Spinner";
import { updateCompletedTask } from "../../../service/Task";
import { uploadFileToS3 } from "../../../service/S3Bucket";
import { toast } from "../../../utils/toast";
import translations from "../../common/Translations";
import { GrPowerReset } from "react-icons/gr";
import Modal from "../../../pages/task/Modal"
import ExitModal from "../../../pages/workflow/ExitModal/ExitModal";
import CommonButton from "../../common/CommonButton";
import FileChooserPreview from "../../common/FileChooserPreview";
import { HiOutlineArrowLongLeft } from "react-icons/hi2";
import { matchesVisibilityRules } from "../../../utils/workflowVisibilityRules";
import {
  isLinkedWorkflowMarkerField,
  splitLinkedWorkflowMarkerFields,
  stripLinkedWorkflowMarkerFields,
} from "../../../utils/workflowLinkedWorkflowRules";
import { fetchWorkflowData } from "../../../service/workflow";

export default function UpdateTask() {
  const { taskId } = useParams();
  const [taskDetail, setTaskDetail] = useState([]);
  const [baseFields, setBaseFields] = useState([]);
  const { theme, langMode, workflowId } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatedFiles, setUpdatedFiles] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [fieldValues, setFieldValues] = useState({});
  const [metaData, setMetaData] = useState([]);
  const [activeRadioInputKey, setActiveRadioInputKey] = useState(null);
  const initialTaskDetailRef = useRef(taskDetail);
  const initialFieldValuesRef = useRef({ ...fieldValues });
  const initialParentSelectionsRef = useRef({});
  const [linkedWorkflowMarkers, setLinkedWorkflowMarkers] = useState([]);
  const [linkedWorkflows, setLinkedWorkflows] = useState({});
  // Cache dependent field values per parent radio option value.
  // Shape: { [parentUniqueKey]: { [optionValue]: { [childUniqueKey]: value } } }
  const dependentValueCacheRef = useRef({});
  const originalValuesRef = useRef({});
  const [open, setOpen] = useState(false)
  const [img, setImg] = useState('')
  const [formKey, setFormKey] = useState(Date.now());
  const [exitModal, setExitModal] = useState(false);
  const [nextPath, setNextPath] = useState(null);

  const handleOpenModal = (img) => {
    setOpen(true)
    setImg(img)
  }

  const getValueOrInputs = (data) => {
    const { value, allInputValues } = data || {};
    if (!value) {
      return;
    }
    if (allInputValues && allInputValues.length > 0) {
      const inputObject = allInputValues.find(item => item.inputValue === value ? item?.inputKey : "");
      return inputObject; // Return an object containing the inputKeys array
    }

    return {};
  };

  const getFilteredDragItems = (dragItems, inputKey) => {
    return dragItems?.filter(item => item?.radioId === inputKey?.inputKey) || [];
  };

  const getSelectedInputKeyForField = (fieldMeta, fieldValue) => {
    if (!fieldMeta?.allInputValues?.length) return null;
    const found = fieldMeta.allInputValues.find((iv) => iv.inputValue === fieldValue);
    return found?.inputKey || null;
  };

  const hasActiveVisibilityRules = (field) => {
    const rules = field?.visibilityRules;
    if (!Array.isArray(rules) || rules.length === 0) return false;
    return rules.some(
      (r) => Array.isArray(r.conditions) && r.conditions.length > 0
    );
  };

  const isIndependentField = (field) => {
    if (!field?.uniqueKey) return false;
    if (isLinkedWorkflowMarkerField(field)) return false;
    if (hasActiveVisibilityRules(field)) return false;
    if (field.radioId != null && String(field.radioId).trim() !== "") return false;
    return true;
  };

  const buildBaseFields = (fieldList, dragItems) => {
    const valueByKey = (fieldList || []).reduce((acc, field) => {
      if (field?.uniqueKey) acc[field.uniqueKey] = field;
      return acc;
    }, {});

    const independentFromMeta = (dragItems || []).filter(isIndependentField);
    if (independentFromMeta.length > 0) {
      const seen = new Set();
      const merged = independentFromMeta.map((metaField) => {
        seen.add(metaField.uniqueKey);
        const fromList = valueByKey[metaField.uniqueKey];
        return {
          ...metaField,
          ...(fromList || {}),
          value: fromList?.value ?? metaField.value ?? null,
        };
      });

      (fieldList || []).forEach((field) => {
        if (field?.uniqueKey && !seen.has(field.uniqueKey) && isIndependentField(field)) {
          merged.push({ ...field, value: field.value ?? null });
        }
      });

      return merged;
    }

    return (fieldList || []).filter(isIndependentField);
  };

  const computeVisibleFields = (baseFields, allMeta, values) => {
    const base = Array.isArray(baseFields) ? baseFields : [];
    const meta = Array.isArray(allMeta) ? allMeta : [];
    const effective = values || {};

    const visible = new Map();
    base.forEach((f) => {
      if (f?.uniqueKey) visible.set(f.uniqueKey, f);
    });

    const shouldShowByLegacyRadioId = (item) => {
      if (!item?.radioId) return false;
      // legacy: show if any parent field selected inputKey equals this radioId
      for (const m of meta) {
        const v = effective[m.uniqueKey];
        const selectedInputKey = getSelectedInputKeyForField(m, v);
        if (selectedInputKey && selectedInputKey === item.radioId) return true;
      }
      return false;
    };

    let changed = true;
    while (changed) {
      changed = false;
      for (const item of meta) {
        if (!item?.uniqueKey || visible.has(item.uniqueKey)) continue;
        const byRules =
          Array.isArray(item.visibilityRules) && item.visibilityRules.length > 0
            ? matchesVisibilityRules(item.visibilityRules, effective)
            : false;
        const byLegacy = shouldShowByLegacyRadioId(item);
        if (byRules || byLegacy) {
          visible.set(item.uniqueKey, item);
          changed = true;
        }
      }
    }

    return Array.from(visible.values());
  };

  const linkedWorkflowIdForMarker = (marker) =>
    marker?.linkedWorkflowId ?? marker?.linked_workflow_id ?? null;

  const activeLinkedWorkflowMarkers = useMemo(
    () =>
      linkedWorkflowMarkers.filter(
        (marker) =>
          Array.isArray(marker?.visibilityRules) &&
          marker.visibilityRules.length > 0 &&
          matchesVisibilityRules(marker.visibilityRules, fieldValues)
      ),
    [linkedWorkflowMarkers, fieldValues]
  );

  const activeLinkedWorkflowIds = useMemo(() => {
    const ids = new Set();
    activeLinkedWorkflowMarkers.forEach((marker) => {
      const linkedId = linkedWorkflowIdForMarker(marker);
      if (linkedId != null && linkedId !== "") ids.add(Number(linkedId));
    });
    return Array.from(ids);
  }, [activeLinkedWorkflowMarkers]);

  const activeLinkedWorkflowKey = activeLinkedWorkflowIds.join(",");

  useEffect(() => {
    const missing = activeLinkedWorkflowIds.filter(
      (id) => !linkedWorkflows[id]
    );
    if (missing.length === 0) return;

    setLinkedWorkflows((prev) => {
      const next = { ...prev };
      missing.forEach((id) => {
        next[id] = { status: "loading" };
      });
      return next;
    });

    missing.forEach(async (id) => {
      try {
        const data = await fetchWorkflowData(id);
        const { markerFields, normalFields } =
          splitLinkedWorkflowMarkerFields(data.workflowFields ?? []);
        const dragItems =
          Array.isArray(normalFields) && normalFields.length > 0
            ? normalFields
            : data.existDragItems?.length
              ? stripLinkedWorkflowMarkerFields(data.existDragItems)
              : [];
        const base = dragItems.filter(isIndependentField);
        setLinkedWorkflows((prev) => ({
          ...prev,
          [id]: {
            status: "loaded",
            name: data.existWorkflowName,
            fields: dragItems,
            baseFields: base,
            markerFields,
          },
        }));
      } catch (err) {
        console.error(`Error fetching linked workflow ${id}:`, err);
        setLinkedWorkflows((prev) => ({
          ...prev,
          [id]: { status: "error", message: err?.message },
        }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLinkedWorkflowKey]);

  const visibleLinkedFieldsByMarker = useMemo(() => {
    const map = new Map();
    activeLinkedWorkflowMarkers.forEach((marker) => {
      const linkedId = Number(linkedWorkflowIdForMarker(marker));
      const entry = linkedWorkflows[linkedId];
      if (!entry?.fields) {
        map.set(marker.uniqueKey, []);
        return;
      }
      const base = entry.baseFields || entry.fields.filter(isIndependentField);
      const visible = computeVisibleFields(base, entry.fields, fieldValues);
      map.set(marker.uniqueKey, visible);
    });
    return map;
  }, [activeLinkedWorkflowMarkers, linkedWorkflows, fieldValues]);

  const activeLinkedWorkflowFields = useMemo(() => {
    const merged = [];
    visibleLinkedFieldsByMarker.forEach((fields) => {
      fields.forEach((field) => {
        if (!isLinkedWorkflowMarkerField(field)) merged.push(field);
      });
    });
    return merged;
  }, [visibleLinkedFieldsByMarker]);

  const linkedWorkflowMeta = useMemo(() => {
    const merged = [];
    activeLinkedWorkflowIds.forEach((id) => {
      const entry = linkedWorkflows[id];
      if (entry?.fields?.length) {
        merged.push(...entry.fields);
      }
    });
    return merged;
  }, [activeLinkedWorkflowIds, linkedWorkflows]);

  useEffect(() => {
    if (!activeLinkedWorkflowFields.length) return;
    setFieldValues((prev) => {
      const next = { ...prev };
      let changed = false;
      activeLinkedWorkflowFields.forEach((field) => {
        if (!field?.uniqueKey || isLinkedWorkflowMarkerField(field)) return;
        if (!Object.prototype.hasOwnProperty.call(next, field.uniqueKey)) {
          next[field.uniqueKey] = field.value ?? "";
          changed = true;
        }
        if (!Object.prototype.hasOwnProperty.call(originalValuesRef.current, field.uniqueKey)) {
          originalValuesRef.current[field.uniqueKey] = field.value ?? "";
        }
      });
      return changed ? next : prev;
    });
  }, [activeLinkedWorkflowFields]);

  const processDragItems = (filteredDragItems) => {
    return filteredDragItems?.map(item => getValueOrInputs(item));
  };

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      setExitModal(true);
      e.returnValue = "";
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    window.history.pushState(null, document.title, window.location.href);
    const handlePopState = (e) => {
      e.preventDefault();
      setExitModal(true);
      e.returnValue = "";
      e.returnValue = "";
    };
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  useEffect(() => {
    const getTaskData = async () => {
      try {
        const taskData = await fetchCompletedTask(taskId);
        const rawFieldList = taskData.body?.value?.fieldList || [];
        const rawDragItems = taskData.body?.value?.dragItems || [];
        const { markerFields: markerFromMeta, normalFields: dragItems } =
          splitLinkedWorkflowMarkerFields(rawDragItems);
        const { markerFields: markerFromList, normalFields: fieldList } =
          splitLinkedWorkflowMarkerFields(rawFieldList);
        const mergedMarkers = new Map();
        markerFromMeta.forEach((field) => {
          if (field?.uniqueKey) mergedMarkers.set(field.uniqueKey, field);
        });
        markerFromList.forEach((field) => {
          if (field?.uniqueKey && !mergedMarkers.has(field.uniqueKey)) {
            mergedMarkers.set(field.uniqueKey, field);
          }
        });
        const linkedMarkers = Array.from(mergedMarkers.values());
        setLinkedWorkflowMarkers(linkedMarkers);

        const combinedMeta = [...dragItems, ...linkedMarkers];
        const independentFields = buildBaseFields(fieldList, combinedMeta);
        const initialValues = {};

        independentFields.forEach((field) => {
          if (field?.uniqueKey) {
            initialValues[field.uniqueKey] = field.value ?? "";
          }
        });
        (combinedMeta || []).forEach((m) => {
          if (isLinkedWorkflowMarkerField(m)) return;
          if (m?.uniqueKey && !(m.uniqueKey in initialValues)) {
            initialValues[m.uniqueKey] = m.value ?? "";
          }
        });
        (fieldList || []).forEach((f) => {
          if (isLinkedWorkflowMarkerField(f)) return;
          if (f?.uniqueKey) initialValues[f.uniqueKey] = f.value ?? "";
        });

        // Persist original values from API for restore/caching (includes dependent fields too).
        const original = {};
        (combinedMeta || []).forEach((m) => {
          if (isLinkedWorkflowMarkerField(m)) return;
          if (m?.uniqueKey) original[m.uniqueKey] = m.value ?? "";
        });
        (fieldList || []).forEach((f) => {
          if (isLinkedWorkflowMarkerField(f)) return;
          if (f?.uniqueKey) original[f.uniqueKey] = f.value ?? "";
        });
        originalValuesRef.current = original;

        setBaseFields(independentFields);
        setMetaData(combinedMeta || []);
        setFieldValues(initialValues);
        if (Object.keys(initialParentSelectionsRef.current).length === 0) {
          // Track initial parent values so we can clear children on change.
          const parents = (combinedMeta || []).filter(
            (m) =>
              m?.fieldType === "radio" &&
              m?.uniqueKey &&
              !isLinkedWorkflowMarkerField(m)
          );
          parents.forEach((p) => {
            initialParentSelectionsRef.current[p.uniqueKey] =
              initialValues[p.uniqueKey] ?? p.value ?? "";
          });
        }

        const visible = computeVisibleFields(
          independentFields,
          combinedMeta,
          initialValues
        );
        setTaskDetail(visible);
        if (!initialTaskDetailRef.current.length) {
          initialTaskDetailRef.current = visible;
        }

        if (Object.keys(initialFieldValuesRef.current).length === 0) {
          initialFieldValuesRef.current = { ...initialValues };
        }
      } catch (error) {
        console.error("Error fetching completed task:", error);
      } finally {
        setLoading(false);
      }
    };

    if (taskId) {
      getTaskData();
    }
  }, [taskId]);


  const handleFileChange = (fieldKey, event, isMulti = false) => {
    const files = Array.from(event.target.files || []);
    const next = isMulti ? files : files[0];
    if (!next || (Array.isArray(next) && next.length === 0)) return;
    setUpdatedFiles((prevFiles) => ({
      ...prevFiles,
      [fieldKey]: next,
    }));
  };

  const handleChange = (fieldKey, event) => {
    setFieldValues((prevValues) => ({
      ...prevValues,
      [fieldKey]: event.target.value,
    }));
  };

  const getAllAncestorSiblings = (currentKey, collected = [], parents = []) => {
    const currentMeta = metaData.find((meta) => meta.uniqueKey === currentKey);
    if (!currentMeta || !currentMeta.radioId) return { collected, parents };

    const parentKey = currentMeta.radioId;
    const siblingFields = metaData.filter(
      (meta) => meta.radioId === parentKey && meta.uniqueKey !== currentKey
    );

    collected = [...collected, ...siblingFields];
    let parentMeta = null;
    for (const meta of metaData) {
      const match = meta.allInputValues?.find(input => input.inputKey === parentKey);
      if (match) {
        parentMeta = meta;
        break;
      }
    }
    if (parentMeta) {
      parents.push(parentMeta);
      return getAllAncestorSiblings(parentMeta.uniqueKey, collected, parents);
    }
    return { collected, parents };
  };

  const checkDependentValue = (item, metaData, updatedFields) => {
    if (item?.allInputValues && item?.allInputValues.length > 0) {
      const result = getValueOrInputs(item);
      const inputKey = result?.inputKey;

      if (inputKey) {
        const dependentFields = metaData.filter(
          (metaItem) => metaItem?.radioId === inputKey
        );

        dependentFields.forEach((childItem) => {
          const isPresent = updatedFields.some(
            (field) => field?.id === childItem?.id
          );

          if (!isPresent) {
            updatedFields.push(childItem);
            // Recurse if child is also a radio
            if (childItem?.fieldType === "radio") {
              checkDependentValue(childItem, metaData, updatedFields);
            }
          }
        });
      }
    }
  };


  const handleRadioChange = (fieldKey, event) => {
    const selectedValue = event.target.value;

    setFieldValues((prev) => {
      const nextValues = { ...prev, [fieldKey]: selectedValue };

      // If user switches the parent radio, clear dependent fields' values
      // so option2 doesn't reuse option1's saved child values.
      const initialParentValue = initialParentSelectionsRef.current[fieldKey];
      const prevSelectedValue =
        Object.prototype.hasOwnProperty.call(prev, fieldKey) && prev[fieldKey] !== undefined
          ? prev[fieldKey]
          : initialParentValue;
      const didChange =
        prevSelectedValue !== undefined && prevSelectedValue !== selectedValue;

      if (didChange) {
        const combinedMetaForDeps = [...metaData, ...linkedWorkflowMeta];
        const parentMeta = combinedMetaForDeps.find(
          (m) => m?.uniqueKey === fieldKey
        );
        const parentOptionKeys = new Set(
          (parentMeta?.allInputValues || []).map((iv) => iv.inputKey)
        );

        const dependentKeys = new Set();
        combinedMetaForDeps.forEach((m) => {
          if (!m?.uniqueKey) return;
          const hasRuleDependency =
            Array.isArray(m.visibilityRules) &&
            m.visibilityRules.some((r) =>
              (r.conditions || []).some((c) => c.sourceUniqueKey === fieldKey)
            );
          const hasLegacyDependency =
            m.radioId && parentOptionKeys.size > 0 && parentOptionKeys.has(m.radioId);

          if (hasRuleDependency || hasLegacyDependency) {
            dependentKeys.add(m.uniqueKey);
          }
        });

        // Save snapshot for the previously selected option
        if (prevSelectedValue != null && prevSelectedValue !== "") {
          if (!dependentValueCacheRef.current[fieldKey]) {
            dependentValueCacheRef.current[fieldKey] = {};
          }
          const snapshot = {};
          dependentKeys.forEach((k) => {
            // Persist current value (prefer local edits, fall back to original field value)
            const currentLocal = Object.prototype.hasOwnProperty.call(prev, k)
              ? prev[k]
              : undefined;
            snapshot[k] =
              currentLocal !== undefined
                ? currentLocal
                : (originalValuesRef.current?.[k] ?? "");
          });
          dependentValueCacheRef.current[fieldKey][prevSelectedValue] = snapshot;
        }

        // Restore snapshot for the newly selected option (if exists),
        // otherwise clear to empty.
        const restore =
          dependentValueCacheRef.current?.[fieldKey]?.[selectedValue] || null;

        dependentKeys.forEach((k) => {
          nextValues[k] = restore && Object.prototype.hasOwnProperty.call(restore, k)
            ? restore[k]
            : "";
        });

        setUpdatedFiles((prevFiles) => {
          const nextFiles = { ...prevFiles };
          dependentKeys.forEach((k) => {
            if (nextFiles[k]) delete nextFiles[k];
          });
          return nextFiles;
        });

        // Inputs are mostly uncontrolled (defaultValue). Force remount so cleared
        // values reflect immediately in the UI.
        setFormKey(Date.now());
      }

      // Recompute visible fields using visibilityRules (and legacy radioId fallback).
      const visible = computeVisibleFields(baseFields, metaData, nextValues).map(
        (f) =>
          Object.prototype.hasOwnProperty.call(nextValues, f.uniqueKey)
            ? { ...f, value: nextValues[f.uniqueKey] }
            : f
      );
      setTaskDetail(visible);

      return nextValues;
    });
  };

  const handleSubmit = async () => {
    const fieldsData = { taskId };
    setIsLoading(true);
    const seen = new Set();
    const allFields = [...taskDetail, ...activeLinkedWorkflowFields];

    for (const field of allFields) {
      if (isLinkedWorkflowMarkerField(field)) continue;
      if (!field?.uniqueKey || seen.has(field.uniqueKey)) continue;
      seen.add(field.uniqueKey);
      const isFileField = field?.fieldType === "file" || field?.fieldType === "multi_image";
      if (isFileField && updatedFiles[field.uniqueKey]) {

        try {
          if (field.fieldType === "multi_image") {
            const uploadedPaths = await Promise.all(
              updatedFiles[field.uniqueKey].map((file) => uploadFileToS3(file))
            );
            fieldsData[field.uniqueKey] = uploadedPaths.join(",");
          } else {
            const uploadedFilePath = await uploadFileToS3(
              updatedFiles[field.uniqueKey]
            );
            fieldsData[field.uniqueKey] = uploadedFilePath;
          }
        } catch (uploadError) {
          console.error(`Failed to upload file:`, uploadError);
          toast.error(translations[langMode].erroruploadingfileplztry);
          return;
        }
      } else {
        fieldsData[field?.uniqueKey] =
          fieldValues[field?.uniqueKey] || field?.value;
      }
    }
    try {
      const response = await updateCompletedTask(fieldsData);
      if (response?.header?.code === 600) {
        toast.success(response.body.value);
        setExitModal(false);
        setIsLoading(false);
        if (nextPath) {
          navigate(nextPath); // go to originally intended route
          setNextPath(null);
        } else {
          navigate("/task-manage");
        }
        setIsLoading(false);
      } else {
        toast.error(response?.body?.value || "Error updating task. Please try again.");
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Failed to update task:", error);
      toast.error(error?.message || "Error updating task. Please try again.");
      setIsLoading(false);
    }
  };

  const hasUserChangedAnything = (
    taskDetail,
    fieldValues,
    updatedFiles,
    extraFields = []
  ) => {
    const allFields = [...(taskDetail || []), ...(extraFields || [])];
    return allFields?.some((field) => {
      if (!field || !field.uniqueKey) return false;
      if (isLinkedWorkflowMarkerField(field)) return false;

      const { uniqueKey, value, fieldType } = field;

      if (fieldType === "file" || fieldType === "multi_image") {
        const updated = updatedFiles[uniqueKey];
        return (
          (value === null || value === "" || value === undefined) &&
          !!updated &&
          (Array.isArray(updated) ? updated.length > 0 : true)
        );
      } else {
        const original = String(value ?? "").trim();
        const current = String(fieldValues[uniqueKey] ?? "").trim();
        return original !== current;
      }
    });
  };


  useEffect(() => {
    const changed = hasUserChangedAnything(
      taskDetail,
      fieldValues,
      updatedFiles,
      activeLinkedWorkflowFields
    );
    const handleBlockNavigation = async (e) => {
      console.log("Mismatches found: AAAA", changed);
      console.log("?? User has unsaved changes.");
      setExitModal(true);
      setNextPath(e.detail?.nextPath); // Save target route for later

    };

    window.addEventListener("blockNavigation", handleBlockNavigation);

    return () => {
      window.removeEventListener("blockNavigation", handleBlockNavigation);
    };
  }, []);

  // Browsers render number/date/time inputs blank when the value isn't in their exact
  // format (e.g. "NEDC1001" in a number field), so fall back to text for such saved values.
  const getInputType = (field) => {
    const savedValue = String(field?.value ?? "").trim();
    const formatCheck = {
      number: (v) => Number.isFinite(Number(v)),
      date: (v) => /^\d{4}-\d{2}-\d{2}$/.test(v),
      time: (v) => /^\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(v),
    }[field?.fieldType];
    if (formatCheck) {
      return savedValue === "" || formatCheck(savedValue) ? field.fieldType : "text";
    }
    return ["email", "tel"].includes(field?.fieldType) ? field.fieldType : "text";
  };

  // Multi-value answers (checkbox) may arrive as an array, a JSON array string or "A,B".
  const parseMultiValue = (value) => {
    if (Array.isArray(value)) return value.map((v) => String(v).trim());
    const str = String(value ?? "").trim();
    if (str.startsWith("[")) {
      try {
        const parsed = JSON.parse(str);
        if (Array.isArray(parsed)) return parsed.map((v) => String(v).trim());
      } catch (e) {
        // not JSON, fall through to comma split
      }
    }
    return str.split(",").map((v) => v.trim()).filter(Boolean);
  };

  // Keep a saved answer visible even if it's no longer one of the workflow's options.
  const withSavedOptions = (options, savedValues) => {
    const list = Array.isArray(options) ? [...options] : [];
    savedValues.forEach((saved) => {
      if (saved !== "" && !list.some((o) => o.inputValue === saved)) {
        list.push({ inputKey: `saved-${saved}`, id: `saved-${saved}`, inputValue: saved });
      }
    });
    return list;
  };

  const renderField = (field, index, options = {}) => {
    if (!field) return null;
    const showIndex = options?.showIndex !== false;
    const labelPrefix = showIndex && typeof index === "number" ? `${index + 1}. ` : "";

    if (isLinkedWorkflowMarkerField(field)) {
      const linkedId = Number(linkedWorkflowIdForMarker(field));
      const entry = linkedWorkflows[linkedId];
      const visibleLinkedFields =
        visibleLinkedFieldsByMarker.get(field.uniqueKey) || [];
      const linkedName =
        entry?.name ?? (Number.isFinite(linkedId) ? `Workflow ${linkedId}` : "");

      return (
        <div
          key={field.uniqueKey}
          className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3"
        >
          <p className="text-xs font-semibold text-slate-700 mb-2">
            {translations[langMode]?.linkedWorkflowLinkedTo ?? "Linked workflow"}
            {linkedName ? `: ${linkedName}` : ""}
          </p>
          {entry?.status === "loading" && (
            <p className="text-xs text-slate-400">
              {translations[langMode]?.linkedWorkflowLoading ??
                "Loading workflows..."}
            </p>
          )}
          {entry?.status === "error" && (
            <p className="text-xs text-red-500">
              {translations[langMode]?.linkedWorkflowLoadFailed ??
                "Couldn't load this linked workflow."}
            </p>
          )}
          {entry?.status === "loaded" && visibleLinkedFields.length === 0 && (
            <p className="text-xs text-slate-400">
              {translations[langMode]?.linkedWorkflowNoFields ??
                "This workflow has no fields to preview."}
            </p>
          )}
          {entry?.status === "loaded" &&
            visibleLinkedFields.map((linkedField, idx) =>
              renderField(linkedField, idx, { showIndex: true })
            )}
        </div>
      );
    }

    switch (field?.fieldType) {
      case "text":
      case "email":
      case "tel":
      case "number":
      case "date":
      case "time":
      case "scan":
      case "ocr":
      case "gps":
      default: {
        // Unknown field types also land here so their value is never hidden.
        const inputType = getInputType(field);
        return (
          <div key={field.uniqueKey} className="mb-4">
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              {labelPrefix}
              {field.fieldName}
            </label>
            <input
              type={inputType}
              defaultValue={fieldValues[field.uniqueKey] ?? field.value ?? ""}
              key={field.uniqueKey}
              onChange={(e) => handleChange(field.uniqueKey, e)}
              className={`border border-slate-200 ${langMode === 'ar' ? 'text-sm' : 'text-sm'} px-3 py-2 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-[#236DB4]/30 focus:border-[#236DB4] transition`}
            />
          </div>
        );
      }
      case "textarea":
        return (
          <div key={field.uniqueKey} className="mb-4">
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              {labelPrefix}
              {field.fieldName}
            </label>
            <textarea
              defaultValue={fieldValues[field.uniqueKey] ?? field.value ?? ""}
              key={field.uniqueKey}
              onChange={(e) => handleChange(field.uniqueKey, e)}
              className={`border border-slate-200 px-3 py-2 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-[#236DB4]/30 focus:border-[#236DB4] transition ${langMode === 'ar' ? 'text-sm' : 'text-sm'}`}
              rows="4"
            ></textarea>
          </div>
        );
      case "radio":
        const hasLocalRadioValue = Object.prototype.hasOwnProperty.call(
          fieldValues,
          field.uniqueKey
        );
        return (
          <div key={field.uniqueKey} className="mb-4">
            <p className="text-xs font-semibold text-slate-700 mb-1.5">
              {field.fieldName}
            </p>
            <div className="flex flex-col gap-1.5">
              {withSavedOptions(field.allInputValues, [String(field.value ?? "")]).map((option) => (
                <label key={option.inputKey} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                  <input
                    type="radio"
                    name={field.uniqueKey}
                    value={option.inputValue}
                    checked={
                      hasLocalRadioValue
                        ? fieldValues[field.uniqueKey] === option.inputValue
                        : field.value === option.inputValue
                    }
                    onChange={(e) => handleRadioChange(field.uniqueKey, e)}
                    className={`h-3.5 w-3.5 shrink-0 accent-[#236DB4] ${langMode === 'ar ml-4' ? 'text-md' : 'text-md'}`}
                  />
                  <span className={`${langMode === 'ar' ? 'text-sm' : 'text-sm'} `}>{option.inputValue}</span>
                </label>
              ))}
            </div>
          </div>
        );


      case "checkbox":
        // Workflow checkboxes define options; show each one ticked if it was submitted.
        if (Array.isArray(field.allInputValues) && field.allInputValues.length > 0) {
          const selected = parseMultiValue(field.value);
          return (
            <div key={field.uniqueKey} className="mb-4">
              <p className="text-xs font-semibold text-slate-700 mb-1.5">
                {labelPrefix}{field.fieldName}
              </p>
              <div className="flex flex-col gap-1.5">
                {withSavedOptions(field.allInputValues, selected).map((option) => (
                  <label key={option.inputKey} className="flex items-center gap-2 text-sm text-slate-600">
                    <input
                      type="checkbox"
                      checked={selected.includes(String(option.inputValue).trim())}
                      readOnly
                      disabled
                      className="h-4 w-4 accent-[#236DB4] rounded"
                    />
                    <span className="text-sm">{option.inputValue}</span>
                  </label>
                ))}
              </div>
            </div>
          );
        }
        return (
          <div key={field.uniqueKey} className="mb-4 flex items-center">
            <input
              type="checkbox"
              defaultChecked={field.value === "true" || field.value === true}
              key={field.uniqueKey}
              className="mr-2 h-4 w-4 accent-[#236DB4] rounded"
              disabled
            />
            <label className="text-sm text-slate-600">
              {labelPrefix}
              {field.fieldName}
            </label>
          </div>
        );
      case "file":
      case "multi_image":
        return (
          <div key={field.uniqueKey} className="mb-4">
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              {labelPrefix}
              {field.fieldName}
            </label>
            {field.value ? (
              <div className="mt-2" >
                {field.fieldType === "multi_image" ? (
                  <div className="flex flex-wrap gap-2">
                    {String(field.value)
                      .split(",")
                      .map((v) => v.trim())
                      .filter(Boolean)
                      .map((url) => (
                        <div
                          key={url}
                          className="w-24 h-24 border border-slate-200 rounded-lg overflow-hidden"
                        >
                          <img
                            src={url}
                            alt="Uploaded image preview"
                            className="w-full h-full object-cover"
                            onClick={() => handleOpenModal(url)}
                          />
                        </div>
                      ))}
                  </div>
                ) : field.value.match(/\.(jpeg|jpg|gif|png|svg|webp)$/i) ? (
                  <div className="w-32 h-32 border border-slate-200 rounded-lg overflow-hidden">
                    <img
                      src={field.value}
                      alt="Uploaded file preview"
                      className="w-full h-full object-cover"
                      onClick={() => handleOpenModal(field.value)}
                    />
                  </div>
                ) : (
                  <a
                    href={field.value}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#236DB4] underline hover:text-[#1d5a96] mb-2 block"
                  >
                    {translations[langMode].viewexistingfile}
                  </a>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-400 mb-2">{translations[langMode].nofileuploaded}</p>
            )}
            <FileChooserPreview
              id={field.uniqueKey}
              multiple={field.fieldType === "multi_image"}
              accept={field.accept || "image/*"}
              onChange={(e) =>
                handleFileChange(field.uniqueKey, e, field.fieldType === "multi_image")
              }
              theme={theme}
              size="md"
              className="mt-1"
              chooseFileText={translations[langMode]?.chooseFile ?? "Choose File"}
              fileName={
                (Array.isArray(updatedFiles[field.uniqueKey])
                  ? updatedFiles[field.uniqueKey].map((file) => file.name).join(", ")
                  : updatedFiles[field.uniqueKey]?.name) ||
                (translations[langMode]?.noFile ?? "No file chosen")
              }
            />
          </div>
        );

      case "select":
        return (
          <div key={field.uniqueKey} className="mb-4">
            <label className="block text-sm font-medium text-slate-700">
              {labelPrefix}
              {field.fieldName}
            </label>
            <select
              value={field.value || ""}
              disabled
              className={`border border-slate-200 p-2 mt-1 w-full rounded-lg bg-slate-100 focus:outline-none ${langMode === 'ar' ? 'text-sm' : 'text-md'}`}
            >
              {(Array.isArray(field.allInputValues) && field.allInputValues.length > 0) || field.value ? (
                <>
                  {!field.value && <option value="" />}
                  {withSavedOptions(field.allInputValues, [String(field.value ?? "")]).map((option) => (
                    <option key={option.id ?? option.inputKey} value={option.inputValue}>
                      {option.inputValue}
                    </option>
                  ))}
                </>
              ) : (
                <option value="" disabled>
                  {translations[langMode].nooptionavailable}
                </option>
              )}
            </select>
          </div>
        );

    }
  };

  if (loading) {
    return <div className="text-center p-4">{translations[langMode].loadingtask}</div>;
  }

  if (error) {
    return <div className="text-center p-4 text-red-500">{error}</div>;
  }
  const handleGoBack = () => {
    navigate("/task-manage");
    return (
      <div>
        <Spinner />
      </div>
    );
  };

  const handleReset = () => {
    setTaskDetail([...initialTaskDetailRef.current]); // Reset task details
    setFieldValues({ ...initialFieldValuesRef.current }); // Reset field values
    setUpdatedFiles({}); // Clear uploaded files
    setFormKey(Date.now()); // Force re-render of form fields
    toast.info("Task details have been reset to default.");
  };

  const navigateToWorkflow = () => {
    handleSubmit()
  };

  const handleExitModal = () => {
    setExitModal(true);
  };

  return (
    <div className="bg-slate-50 min-h-full">
      {isLoading && <Spinner />}
      <div className="flex justify-between items-center px-5 pt-5">
        <div className='flex items-center gap-3'>
          <button
            onClick={handleExitModal}
            className="
                 rounded-lg
                 bg-blue-50
                 text-[#236DB4]
                 hover:bg-blue-100
                 focus:outline-none
                 focus:ring-2
                 focus:ring-[#236DB4]/30
                 transition
                 duration-200
                 ease-in-out
                 flex items-center justify-center
                 w-9 h-9
                 "
            aria-label="Go back"
          >
            <HiOutlineArrowLongLeft size={20} />
          </button>
          <div>
            <p className='text-lg font-bold text-slate-800 leading-tight'>{translations[langMode].updatetaskdetail}</p>
          </div>
        </div>
        <div className="">
          <CommonButton colorIntent="warning" tooltipText={translations[langMode].resetthecompletiondata} tooltipPosition="left" onClick={handleReset} variant="success" ><GrPowerReset size={16} className="" /> {translations[langMode].reset}</CommonButton>
        </div>
      </div>
      <div className="p-5">
        <div className="max-w-full rounded-xl border border-slate-100 bg-white p-6">
          {taskDetail.length === 0 ? (
            <div className="text-center text-slate-400 text-sm py-6">No data available</div>
          ) : (
            <form key={formKey} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-x-6 gap-y-5">
              {Array.isArray(taskDetail) && taskDetail?.map((field, index) => renderField(field, index))}
            </form>
          )}
        </div>
        <div className="flex gap-2.5 justify-end mt-5">
          <CommonButton colorIntent="danger"
            variant="danger"
            onClick={handleGoBack}
            tooltipText={translations[langMode].gobacktheprivious}
            tooltipPosition="top">
            {translations[langMode].cancel}
          </CommonButton>
          <CommonButton colorIntent="success"
            variant="primary"
            type="submit"
            onClick={handleSubmit}
            tooltipText={translations[langMode].savetheupdateddatacompleted}
            tooltipPosition="top"
          >
            {translations[langMode].save}
          </CommonButton>

        </div>
      </div>
      {open && <Modal open={open} setOpen={setOpen} img={img} />
      }

      {exitModal && (
        <ExitModal
          setExitModal={setExitModal}
          exitModal={exitModal}
          navigateToWorkflow={navigateToWorkflow}
          showMsg={translations[langMode].discardMessage}
          cancel_btn={translations[langMode].discard}
          save_btn={translations[langMode].save}
          onCancel={() => { setExitModal(false); nextPath ? navigate(nextPath) : navigate("/task-manage") }}
        />
      )}
    </div>
  );
}
